import { Inject, Injectable } from '@angular/core';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  tap,
} from 'rxjs/operators';
import { combineLatest, Observable, of } from 'rxjs';
import {
  CultureInfo,
  CultureInfoService,
  LogService,
  TOKENS as COMMON_TOKENS,
} from '@trustedshops/tswp-core-common';
import {
  ChannelMatchMode,
  ProductFeatureService,
  TOKENS as MASTERDATA_TOKENS,
} from '@trustedshops/tswp-core-masterdata';
import { ChannelsService } from './channels.service';
import { UserService } from './user.service';
import { Account, AccountService } from './account.service';
import { RxJsSubjectBridge } from '@trustedshops/tswp-core-common-eventing-rxjs';

/**
 * Metadata fields for Ada chatbot integration.
 * All fields use snake_case to match Ada's expected format.
 */
export interface ChatbotMetaFields {
  // User-specific fields
  created_at?: string; // ISO date when user was created
  email?: string; // User's email address
  first_name?: string; // User's first name
  last_name?: string; // User's last name
  locale_code?: string; // User's locale (e.g., 'en-GB')

  // Channel-specific fields
  channel_id?: string; // Selected channel identifier
  has_reviews?: boolean; // Whether channel has reviews enabled

  // Account fields
  account_created_at?: string; // ISO date when account was created
  account_id?: string; // Unique account identifier
}

/**
 * Ada chatbot settings structure
 */
interface AdaSettings {
  metaFields: ChatbotMetaFields;
}

/**
 * Window interface extended with Ada chatbot integration
 */
export interface AdaWindow extends Window {
  adaSettings?: AdaSettings;
}

/**
 * Service responsible for syncing user metadata with Ada chatbot.
 * Aggregates data from multiple sources and pushes it to the chatbot widget.
 */
@Injectable()
export class ChatbotService {
  static readonly TYPE = 'ChatbotService';
  private static readonly FEAT_CHATBOT = 'FEAT_CHATBOT';

  private static formatError(error: unknown): string {
    return error instanceof Error ? error.message : String(error);
  }

  constructor(
    @Inject(COMMON_TOKENS.LogService)
    private readonly logService: LogService,
    @Inject(MASTERDATA_TOKENS.ProductFeatureService)
    private readonly productFeatureService: ProductFeatureService,
    @Inject(COMMON_TOKENS.CultureInfoService)
    private readonly cultureInfoService: CultureInfoService,
    private readonly channelsService: ChannelsService,
    private readonly userService: UserService,
    private readonly accountService: AccountService
  ) {}

  /**
   * Initialize the chatbot metadata synchronization.
   * Gates on FEAT_CHATBOT — skips initialization entirely when the feature is absent.
   * Sets up a reactive stream that updates Ada whenever relevant data changes.
   * Only re-syncs when the channel_id changes to avoid unnecessary updates.
   */
  public async init(): Promise<void> {
    try {
      const hasChatbot = await this.productFeatureService.hasFeature({
        featureNames: ChatbotService.FEAT_CHATBOT,
        channelMatch: ChannelMatchMode.Some,
      });

      if (!hasChatbot) {
        this.logService.debug(
          ChatbotService.TYPE,
          `${ChatbotService.FEAT_CHATBOT} not present, skipping chatbot initialization.`
        );
        return;
      }
    } catch (error: unknown) {
      this.logService.error(
        ChatbotService.TYPE,
        new Error(
          `Failed to fetch PFM features for chatbot gate: ${ChatbotService.formatError(
            error
          )}`
        )
      );
      return;
    }

    this.getMetadataStream()
      .pipe(
        // Only emit when channel changes (primary identifier for updates)
        distinctUntilChanged(
          (prev, curr) => prev.channel_id === curr.channel_id
        ),
        // Push metadata to Ada chatbot
        tap((metadata) => this.syncChatbotMetadata(metadata)),
        // Handle any errors gracefully without breaking the stream
        catchError((error: unknown) => {
          this.logService.error(
            ChatbotService.TYPE,
            new Error(
              `Chatbot initialization failed: ${ChatbotService.formatError(
                error
              )}`
            )
          );
          return of(undefined);
        })
      )
      .subscribe();
  }

  /**
   * Pushes metadata to the Ada chatbot by setting it on the global window object.
   * Ada's JavaScript SDK reads from window.adaSettings.metaFields.
   *
   * @param metaFields - The metadata to sync with Ada
   */
  private syncChatbotMetadata(metaFields: ChatbotMetaFields): void {
    try {
      // Ada expects settings to be available on window.adaSettings
      (window as AdaWindow).adaSettings = { metaFields };

      this.logService.debug(ChatbotService.TYPE, 'Metadata synced', {
        metaFields,
      });
    } catch (error: unknown) {
      this.logService.error(
        ChatbotService.TYPE,
        new Error(
          `Failed to sync metadata: ${ChatbotService.formatError(error)}`
        )
      );
    }
  }

  /**
   * Creates a combined stream of all metadata from various sources.
   * Merges user, account, channel, and locale data into a single object.
   *
   * @returns Observable that emits complete metadata whenever any source updates
   */
  private getMetadataStream(): Observable<ChatbotMetaFields> {
    return combineLatest([
      this.getUserData(),
      this.getAccountData(),
      this.getChannelData(),
      this.getLocaleCode(),
    ]).pipe(
      // Transform the combined data into the format Ada expects
      map(([user, account, channel, localeCode]) => ({
        // User fields - basic user information
        email: user.email,
        first_name: user.given_name,
        last_name: user.family_name,
        created_at: user.createdAt,
        locale_code: localeCode,

        // Account fields
        account_id: account.accountId,
        account_created_at: account.createdAt,

        // Channel fields - selected channel context
        channel_id: channel.channelId,
        has_reviews: channel.hasReviews,
      }))
    );
  }

  /**
   * Retrieves user data from the user service.
   * @returns Observable of user information
   */
  private getUserData() {
    return this.userService.user$;
  }

  /**
   * Retrieves account data.
   * Filters out the initial `undefined` value from {@link AccountService.account$}
   * so downstream `combineLatest` only fires once a real account is available.
   *
   * @returns Observable of account data
   */
  private getAccountData(): Observable<Account> {
    return this.accountService.account$.pipe(
      filter((account): account is Account => !!account)
    );
  }

  /**
   * Retrieves channel-specific data.
   * Combines the selected channel ID with its review status.
   *
   * @returns Observable of channel data
   */
  private getChannelData() {
    return combineLatest([
      this.channelsService.selectedChannelId$,
      this.channelsService.selectedChannelHasReviews$,
    ]).pipe(map(([channelId, hasReviews]) => ({ channelId, hasReviews })));
  }

  /**
   * Retrieves the user's locale code from culture settings.
   * Falls back to 'en-GB' if culture info cannot be retrieved.
   *
   * @returns Observable of locale code string
   */
  private getLocaleCode(): Observable<string> {
    return this.cultureInfoService.currentCulture
      .convertWith<Observable<CultureInfo>>(RxJsSubjectBridge)
      .pipe(
        // Extract the IETF language tag (e.g., 'en-GB', 'de-DE')
        map((culture) => culture.ietfLanguageTag),
        // Only emit when locale actually changes
        distinctUntilChanged(),
        // Default to English (GB) if culture service fails
        catchError(() => of('en-GB'))
      );
  }
}
